/**
 * Status vocabularies, in one place.
 *
 * A tone is a judgement, not a lookup: `on_hold` is a warning for a client
 * relationship and neutral for a project that was paused on purpose. So they
 * are declared here per domain rather than derived from the string, and the
 * shared `StatusChip` is given the map.
 *
 * Labels are supplied only where the raw value reads badly. `StatusChip` falls
 * back to sentence case, so `part_paid` becomes "Part paid" without a line
 * here — and a label that says the same thing is a second place to change it.
 */

import type { Tone } from '@connect/web-shared';

// ── crm ─────────────────────────────────────────────────────
export const CLIENT_STATUS_TONES: Record<string, Tone> = {
  prospect: 'info',
  active: 'success',
  on_hold: 'warning',
  former: 'neutral',
};

// ── projects ────────────────────────────────────────────────
export const PROJECT_STATUS_TONES: Record<string, Tone> = {
  draft: 'neutral',
  active: 'success',
  on_hold: 'warning',
  completed: 'info',
  cancelled: 'danger',
};

export const TASK_STATUS_TONES: Record<string, Tone> = {
  todo: 'neutral',
  in_progress: 'accent',
  blocked: 'danger',
  review: 'warning',
  done: 'success',
  cancelled: 'neutral',
};

export const TASK_STATUS_LABELS: Record<string, string> = {
  in_progress: 'In progress',
};

export const PRIORITY_TONES: Record<string, Tone> = {
  low: 'neutral',
  normal: 'neutral',
  high: 'warning',
  urgent: 'danger',
};

export const MILESTONE_STATUS_TONES: Record<string, Tone> = {
  pending: 'neutral',
  reached: 'success',
  invoiced: 'info',
  cancelled: 'danger',
};

export const BILLING_TYPE_LABELS: Record<string, string> = {
  time_and_materials: 'Time & materials',
  fixed_price: 'Fixed price',
  retainer: 'Retainer',
  non_billable: 'Non-billable',
};

// ── support ─────────────────────────────────────────────────
export const TICKET_STATUS_TONES: Record<string, Tone> = {
  new: 'info',
  open: 'accent',
  waiting_on_client: 'warning',
  resolved: 'success',
  closed: 'neutral',
};

/**
 * The staff wording.
 *
 * The client portal says "Waiting on you" for the same value, and that is not a
 * discrepancy — it is the same fact addressed to two different readers. Which
 * is exactly why each app declares its own labels instead of the API sending
 * one string for both.
 */
export const TICKET_STATUS_LABELS: Record<string, string> = {
  waiting_on_client: 'Waiting on client',
};

// ── time ────────────────────────────────────────────────────
export const SHEET_STATUS_TONES: Record<string, Tone> = {
  draft: 'neutral',
  submitted: 'warning',
  approved: 'success',
  rejected: 'danger',
};

export const CLAIM_STATUS_TONES: Record<string, Tone> = {
  ...SHEET_STATUS_TONES,
  paid: 'info',
};

// ── finance ─────────────────────────────────────────────────
export const INVOICE_STATUS_TONES: Record<string, Tone> = {
  draft: 'neutral',
  issued: 'info',
  part_paid: 'warning',
  paid: 'success',
  overdue: 'danger',
  void: 'neutral',
  credited: 'neutral',
};

export const PAYMENT_METHOD_LABELS: Record<string, string> = {
  bank_transfer: 'Bank transfer',
  card: 'Card',
  cash: 'Cash',
  cheque: 'Cheque',
  other: 'Other',
};

export const LINE_KIND_LABELS: Record<string, string> = {
  time: 'Time',
  expense: 'Expense',
  milestone: 'Milestone',
  fixed: 'Fixed fee',
  adjustment: 'Adjustment',
};

/**
 * Turn a server option list into one the form controls can use.
 *
 * The options endpoints send `{value, label}` already; this exists for the
 * handful of places that hold a bare list of values.
 */
export function toOptions(values: readonly string[]): { value: string; label: string }[] {
  return values.map((value) => ({
    value,
    label: value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' '),
  }));
}

// `formatMinutes` was here. It duplicated `formatDuration` from
// `@connect/web-shared` and got the negative case wrong: `Math.floor` moves a
// negative hour count away from zero while `%` keeps the minute's sign, so the
// two compounded and -30 minutes rendered as `-1h -30m`. Use `formatDuration`,
// which takes the sign off first.
